import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Context Providers
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { FavouritesProvider } from "./context/FavouritesContext";
import { ThemeProvider } from "./context/ThemeContext";

// Layout Components
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { PageLoader } from "./components/LoadingSpinner";

// Pages
import HomePage from "./pages/HomePage";
import BrowsePage from "./pages/BrowsePage";
import ProductPage from "./pages/ProductPage";
import StorePage from "./pages/StorePage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProfilePage from "./pages/ProfilePage";
import FavouritesPage from "./pages/FavouritesPage";
import OrdersPage from "./pages/OrdersPage";
import MessagesPage from "./pages/MessagesPage";
import DashboardPage from "./pages/DashboardPage";
import SearchPage from "./pages/SearchPage";
import CommunityPage from "./pages/CommunityPage";
import CommunityPostPage from "./pages/CommunityPostPage";
import NotificationSettings from "./pages/NotificationSettings";

// Protected Route Component
const ProtectedRoute = ({ children, creatorOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (creatorOnly && !user.is_creator) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Guest Only Route (redirect if logged in)
const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout wrapper
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/browse" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/search" element={<Layout><SearchPage /></Layout>} />
      <Route path="/community" element={
        <ProtectedRoute creatorOnly>
          <Layout><CommunityPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/community/post/:postId" element={
        <ProtectedRoute creatorOnly>
          <Layout><CommunityPostPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/product/:id" element={<Layout><ProductPage /></Layout>} />
      <Route path="/store/:id" element={<Layout><StorePage /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={
        <GuestRoute>
          <Layout><LoginPage /></Layout>
        </GuestRoute>
      } />
      <Route path="/register" element={
        <GuestRoute>
          <Layout><RegisterPage /></Layout>
        </GuestRoute>
      } />

      {/* Protected Routes */}
      <Route path="/cart" element={
        <ProtectedRoute>
          <Layout><CartPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/checkout/success" element={
        <ProtectedRoute>
          <Layout><CheckoutSuccessPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/favourites" element={
        <ProtectedRoute>
          <Layout><FavouritesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <Layout><OrdersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout><MessagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages/:partnerId" element={
        <ProtectedRoute>
          <Layout><MessagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <Layout><NotificationSettings /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Creator Only Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute creatorOnly>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <FavouritesProvider>
              <AppRoutes />
              <Toaster position="bottom-right" richColors />
            </FavouritesProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
